// "Let sales assist you" for free-wedge operators.
//
// A free operator has nobody assigned to them. They signed up off a campaign,
// they are running one module, and the moment they want to know what the rest
// of the product costs there is no one in the room. This is that person.
//
// It logs a callback request against their account in the internal sales CRM
// rather than dialling a number: a click-to-call placed from New York or
// Singapore at 3am Indian time reaches nobody and leaves nothing behind, while
// a row in the queue survives the timezone and carries their country, their
// wedge and their number with it.
//
// Mounted only for wedge orgs (user.planKind). Paying customers already have an
// account manager and never see it.

import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Fab,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';

import Iconify from './Iconify';
import useAuth from '../hooks/useAuth';
import axios from '../utils/axios';

const ENDPOINT = '/api/v1/deskos-billing/clients/me/sales-assist/';

// Matches WHEN_CHOICES on the server. Deliberately not free text: "morning"
// means nothing when the person reading it is nine hours away.
const WHEN_OPTIONS = [
  { value: 'now', label: 'As soon as you can' },
  { value: 'today', label: 'Later today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This week' },
];

const ASKED_KEY = 'deskos:sales-assist-asked';
const DAY_MS = 24 * 60 * 60 * 1000;

function askedRecently() {
  try {
    const at = Number(window.localStorage.getItem(ASKED_KEY) || 0);
    return at > 0 && Date.now() - at < DAY_MS;
  } catch (e) {
    return false;
  }
}

export default function SalesAssistFab({ planKind }) {
  const theme = useTheme();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isPhone = useMediaQuery('(max-width:599.95px)');

  const kind = planKind || user?.planKind;

  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phone, setPhone] = useState(user?.mobile || '');
  const [when, setWhen] = useState('now');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  // Null planKind is a full-platform org. They have a named account manager;
  // a "talk to sales" button in their console would be noise.
  if (!kind) return null;

  const handleOpen = () => {
    setError('');
    setDone(false);
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    const number = phone.trim();
    if (!number) {
      setError('Add a number we can reach you on.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(ENDPOINT, { phone: number, when, note: note.trim() });
      try {
        window.localStorage.setItem(ASKED_KEY, String(Date.now()));
      } catch (e) {
        // Private mode or blocked storage. The request still landed, and the
        // only thing lost is the reminder line the next time they open this.
      }
      setDone(true);
      setNote('');
      enqueueSnackbar('Thanks. Someone will call you back.', { variant: 'success' });
    } catch (err) {
      // The axios instance rejects with the response body, not the axios error,
      // so the status code is usually gone by the time it reaches here. A
      // throttle is the one case worth wording ourselves.
      const detail = err?.detail || err?.response?.data?.detail || '';
      const throttled =
        err?.response?.status === 429 || /throttl/i.test(String(detail));
      if (throttled) {
        setError('We already have your request. Someone will call you back.');
      } else {
        setError(
          err?.phone || detail || 'That did not go through. Try again in a moment.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Fab
        variant="extended"
        color="primary"
        onClick={handleOpen}
        aria-label="Talk to sales"
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 24 },
          // On a phone the admin dock floats at the bottom (60 tall, 12 up),
          // so this sits clear above it instead of landing on top of it.
          bottom: isPhone
            ? 'calc(88px + env(safe-area-inset-bottom))'
            : 24,
          zIndex: theme.zIndex.speedDial,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: `0 8px 24px 0 ${alpha(theme.palette.primary.main, 0.36)}`,
          px: 2.25,
        }}
      >
        <Iconify icon="mdi:headset" width={20} height={20} sx={{ mr: 1 }} />
        Talk to sales
      </Fab>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogContent sx={{ p: 3 }}>
          {done ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: 'success.main',
                }}
              >
                <Iconify icon="mdi:check-bold" width={28} height={28} />
              </Box>
              <Typography variant="h6">We have your request</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Someone from the DeskOS team will call you on {phone.trim()}.
              </Typography>
              <Button onClick={() => setOpen(false)} variant="contained" fullWidth>
                Close
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">Let sales assist you</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Tell us where to reach you and someone will call. No forms after
                  this, no demo you have to sit through.
                </Typography>
              </Box>

              {askedRecently() && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  You asked earlier. We still have that request, and asking again
                  just updates it.
                </Typography>
              )}

              <TextField
                label="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Include your country code"
                fullWidth
                autoFocus
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  When suits you
                </Typography>
                <ToggleButtonGroup
                  value={when}
                  exclusive
                  onChange={(e, v) => v && setWhen(v)}
                  size="small"
                  sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderRadius: 1, borderColor: 'divider' } }}
                >
                  {WHEN_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={opt.value}
                      value={opt.value}
                      sx={{ textTransform: 'none', px: 1.5 }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <TextField
                label="Anything we should know (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />

              {error && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {error}
                </Typography>
              )}

              <Stack direction="row" spacing={1.5}>
                <Button onClick={handleClose} fullWidth disabled={saving}>
                  Not now
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  fullWidth
                  disabled={saving}
                >
                  {saving ? 'Sending' : 'Request a call'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

SalesAssistFab.propTypes = {
  // Optional override. Normally read straight off the logged-in user.
  planKind: PropTypes.string,
};
